import { axios, settings, getResponse } from '../../helpers/main';
import { userAuthContextType } from '../../auth/AuthProvider';

const { backend } = settings;

const userService = { user: userAuthContextType }; //eslint-disable-line no-unused-vars
const objectService = getResponse(); //eslint-disable-line no-unused-vars

/**
 * Service to manage communication with the Parameters API.
 * Follows the Single Responsibility Principle (SRP) by separating network logic from the UI.
 */
export const ParameterService = {
    /**
     * Retrieves all parameters and related master data (customers).
     * @param {Object} params - Query parameters.
     * @param {userService} params.auth - User authentication object.
     * @param {number} [params.languageId='en'] - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    getAll: async ({
        auth,
        languageId = 'en',
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.parameters}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                method: backend?.methods?.parameters?.getAll,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Deletes a parameter by its ID.
     * @param {Object} params - Deletion parameters.
     * @param {userService} params.auth - User authentication object.
     * @param {number} params.id - ID of the parameter to delete.
     * @param {number} [params.languageId='en'] - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    deleteAsync: async ({
        auth,
        id,
        languageId = 'en',
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.parameters}/${id}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.delete(requestObj);
    },

    /**
     * Creates a new parameter.
     * @param {Object} params - Creation parameters.
     * @param {userService} params.auth - User authentication object.
     * @param {Object} params.parameter - The parameter data to create.
     * @param {number} [params.languageId='en'] - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    addAsync: async ({
        auth,
        parameter,
        languageId = 'en',
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.parameters}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                data: parameter,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Retrieves a parameter by its ID, with optional details and master data.
     * @param {Object} params - Query parameters.
     * @param {userService} params.auth - User authentication object.
     * @param {number} params.id - Parameter ID.
     * @param {boolean} [params.withDetails=false] - Whether to include parameter details.
     * @param {number} [params.languageId='en'] - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    getById: async ({
        auth,
        id,
        withDetails = false,
        languageId = 'en',
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.parameters}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                method: backend?.methods?.parameters?.getById,
                id: id,
                withDetails: withDetails,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Updates an existing parameter.
     * @param {Object} params - Update parameters.
     * @param {userService} params.auth - User authentication object.
     * @param {number} params.id - Parameter ID.
     * @param {Object} params.parameter - Updated parameter data.
     * @param {number} [params.languageId='en'] - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    update: async ({
        auth,
        id,
        parameter,
        languageId = 'en',
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.parameters}/${id}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                data: parameter,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Generates a PDF report for a parameter.
     * @param {Object} params - Report parameters.
     * @param {userService} params.auth - User authentication object.
     * @param {number} params.id - Parameter ID.
     * @param {string} [params.dateFrom=''] - Start date for the report.
     * @param {string} [params.dateTo=''] - End date for the report.
     * @param {string} [params.languageCode='en'] - Current language code.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    getReportPdfAsync: async ({
        auth,
        id,
        dateFrom = '',
        dateTo = '',
        languageCode = 'en',
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.parameters}/${backend?.methods?.main?.reports?.pdf}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                data: {
                    id: id,
                    dateFrom: dateFrom,
                    dateTo: dateTo,
                },
                languageCode: languageCode,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Deletes a parameter detail.
     * @param {Object} params - Deletion parameters.
     * @param {userService} params.auth - User authentication object.
     * @param {number} params.id - Parameter detail ID.
     * @param {number} [params.languageId='en'] - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    deleteDetail: async ({
        auth,
        id,
        languageId = 'en',
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.parameterDetails}/${id}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.delete(requestObj);
    },

    /**
     * Retrieves a parameter detail by its ID.
     * @param {Object} params - Query parameters.
     * @param {userService} params.auth - User authentication object.
     * @param {number} params.id - Parameter detail ID.
     * @param {number} [params.languageId='en'] - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    getDetailById: async ({
        auth,
        id,
        languageId = 'en',
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.parameterDetails}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                method: backend?.methods?.parameterDetails?.getById,
                id: id,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Creates a new parameter detail.
     * @param {Object} params - Creation parameters.
     * @param {userService} params.auth - User authentication object.
     * @param {Object} params.parameterDetail - The detail data to create.
     * @param {number} [params.languageId='en'] - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    addDetailAsync: async ({
        auth,
        parameterDetail,
        languageId = 'en',
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.parameterDetails}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                data: parameterDetail,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Updates an existing parameter detail.
     * @param {Object} params - Update parameters.
     * @param {userService} params.auth - User authentication object.
     * @param {number} params.id - Detail ID.
     * @param {Object} params.parameterDetail - Updated detail data.
     * @param {number} [params.languageId='en'] - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectService>} Promise with the API response.
     */
    updateDetail: async ({
        auth,
        id,
        parameterDetail,
        languageId = 'en',
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.parameterDetails}/${id}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                data: parameterDetail,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },
};
